import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CommonModule, formatDate } from '@angular/common';
import { Component, Inject, Injectable, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Params, RouterModule } from '@angular/router';

import { RunCardComponent } from '../run-card/run-card.component';
import { RunSource } from '../shared/enums/run-source.enum';
import { PnLDeltaInfo, PnLDeltaService } from '../shared/services/pnl-delta.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import { PnlComponent } from '../pnl/pnl.component';
import { latestBaseCases, runsBefore } from '../shared/utils/base-cases.util';
import { RunStoreService } from './run-store/run-store.service';
import { Run } from './run.model';
import { parseRunName } from '../shared/utils/run-parser.util';

/** The list's search, filters, paging and open run, kept so coming back from a run's pages finds them as left. */
@Injectable({ providedIn: 'root' })
export class RunsViewState {
  saved?: Pick<RunsComponent, 'term' | 'statuses' | 'sources' | 'shown' | 'expandedRunId'>;
}

@Component({
  selector: 'runs',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    RunCardComponent,
    SpinnerComponent,
    StatusBadgeComponent,
    PnlComponent
  ],
  templateUrl: './runs.component.html',
  styleUrls: ['./runs.component.scss']
})
export class RunsComponent implements OnInit, OnDestroy {
  runs!: Observable<Run[] | undefined>;
  pnlDeltas!: Observable<Map<string, PnLDeltaInfo>>;
  baseCases!: Observable<{ current: Run; prior?: Run } | null>;
  mtiBefore!: Observable<Map<string, string>>;
  loadFailed!: Observable<boolean | undefined>;

  /** Filter chips: key (a run or optimisation status, lower case) and label, as the badges say it. */
  readonly statusChips = [['optimal', 'Optimised'], ['infeasible', 'Infeasible'], ['running', 'Running'], ['failure', 'Failure']] as const;
  readonly sourceChips = [[RunSource.Cloud, 'Cloud'], [RunSource.Local, 'Local']] as const;
  /** Chosen chips; empty means all. */
  statuses = new Set<string>();
  sources = new Set<RunSource>();

  term = "";
  expandedRunId: string | null = null;

  /** Runs are listed a page at a time: thousands of rows at once make the page slow to draw. */
  readonly pageSize = 50;
  shown = this.pageSize;

  constructor(
    private runStoreService: RunStoreService,
    private pnlDeltaService: PnLDeltaService,
    private view: RunsViewState,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  ngOnInit() {
    if (this.view.saved) {
      Object.assign(this, this.view.saved);
    }
    this.runs = this.runStoreService.runs$;
    this.loadFailed = this.runStoreService.loadFailed$;
    this.baseCases = this.runs.pipe(map(runs => runs ? latestBaseCases(runs) : null));
    this.mtiBefore = this.runs.pipe(map(runs => runs ? runsBefore(runs) : new Map()));
    // Pre-compute deltas once when runs change (O(n) instead of O(n²))
    this.pnlDeltas = this.runs.pipe(
      map(runs => runs ? this.pnlDeltaService.computeDeltas(runs) : new Map())
    );
  }

  ngOnDestroy() {
    const { term, statuses, sources, shown, expandedRunId } = this;
    this.view.saved = { term, statuses, sources, shown, expandedRunId };
  }

  search(term: string) {
    this.term = term;
    this.resetPaging();
  }

  resetPaging() {
    this.shown = this.pageSize;
  }

  get filtering(): boolean {
    return !!this.term || this.statuses.size > 0 || this.sources.size > 0;
  }

  clearFilters() {
    this.term = '';
    this.statuses.clear();
    this.sources.clear();
    this.resetPaging();
  }

  retry() {
    this.runStoreService.load();
  }

  /** The day a run last moved (the list is newest first by that), for the day headings. */
  dayOf(run: Run): string {
    const t = run.runHistories[0]?.eventTime;
    return t ? new Date(t).toDateString() : '';
  }

  dayLabel(run: Run): string {
    const day = this.dayOf(run);
    if (!day) return 'No events';
    const today = new Date();
    if (day === today.toDateString()) return 'Today';
    if (day === new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1).toDateString()) return 'Yesterday';
    return formatDate(day, 'EEEE d MMMM yyyy', this.locale);
  }

  /** Where a run's MTI link goes: its outputs against the run it's compared with (see runsBefore). */
  mtiQuery(run: Run, before: Map<string, string> | null): Params {
    const prior = before?.get(run.runId);
    return prior ? { before: prior, after: run.runId } : { after: run.runId };
  }

  /** Change in P&L from the base case before, in $M. */
  baseCaseMove(pair: { current: Run; prior?: Run }): number | null {
    const now = pair.current.optimisationResult?.pnL?.value, before = pair.prior?.optimisationResult?.pnL?.value;
    return now == null || before == null ? null : (now - before) / 1e6;
  }

  trackRun(_: number, run: Run): string { return run.runId; }

  onToggle(runId: string) {
    this.expandedRunId = this.expandedRunId === runId ? null : runId;
  }

  toggleStatus(key: string) { this.toggle(this.statuses, key); }
  toggleSource(key: RunSource) { this.toggle(this.sources, key); }

  private toggle<T>(set: Set<T>, key: T) {
    if (!set.delete(key)) set.add(key);
    this.resetPaging();
  }

  /** The runs the filters and search let through. Empty filter sets mean all. */
  visible(runs: Run[]): Run[] {
    const term = this.term.trim().toLowerCase();
    return runs.filter(run => {
      const latest = run.runHistories[0]?.status.toLowerCase();
      const status = this.statuses.size === 0
        || this.statuses.has(run.optimisationResult?.status.toLowerCase() ?? '')
        || this.statuses.has(latest === 'started' ? 'running' : latest ?? ''); // only started counts as running
      const source = this.sources.size === 0
        || this.sources.has(parseRunName(run.name).source === RunSource.Local ? RunSource.Local : RunSource.Cloud);
      const match = !term || run.name.toLowerCase().includes(term) || run.initiatedBy.toLowerCase().includes(term);
      return status && source && match;
    });
  }
}
